import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { Loader2, LogIn, ArrowLeft } from 'lucide-react';
import useSEO from '@/hooks/useSEO';

export default function AdminLoginPage() {
    useSEO({
        title: 'Admin Login',
        description: 'VVVSI Trust admin console login.',
        noindex: true,
    });
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await login(email, password);
            toast.success('Welcome back!');
            navigate('/admin');
        } catch (err) {
            const detail = err?.response?.data?.detail || 'Invalid credentials';
            toast.error(detail);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="admin-scope min-h-screen flex items-center justify-center bg-[hsl(var(--background))] p-4"
            data-testid="admin-login-page"
        >
            <div className="w-full max-w-md">
                <Link
                    to="/"
                    className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4"
                    data-testid="admin-login-back-home"
                >
                    <ArrowLeft className="h-4 w-4" /> Back to site
                </Link>
                <Card className="bg-card border-border shadow-[0_10px_30px_rgba(15,23,42,0.08)]">
                    <CardContent className="p-8">
                        <div className="h-12 w-12 rounded-xl bg-[hsl(var(--primary))] text-primary-foreground flex items-center justify-center font-display font-bold text-lg mb-5">
                            V
                        </div>
                        <h1 className="font-display text-2xl font-semibold mb-1">
                            VVVSI Admin Console
                        </h1>
                        <p className="text-sm text-muted-foreground mb-6">
                            Sign in to manage content, submissions and site settings.
                        </p>
                        <form onSubmit={submit} className="space-y-4">
                            <div>
                                <Label htmlFor="admin-email">Email</Label>
                                <Input
                                    id="admin-email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    data-testid="admin-login-email-input"
                                />
                            </div>
                            <div>
                                <Label htmlFor="admin-password">Password</Label>
                                <Input
                                    id="admin-password"
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    data-testid="admin-login-password-input"
                                />
                            </div>
                            <Button
                                type="submit"
                                disabled={submitting}
                                className="w-full bg-[hsl(var(--primary))] text-primary-foreground hover:bg-[hsl(var(--primary))]/90"
                                data-testid="admin-login-submit-button"
                            >
                                {submitting ? (
                                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                                ) : (
                                    <LogIn className="h-4 w-4 mr-1.5" />
                                )}
                                Sign in
                            </Button>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
